
import React from 'react';
import { SimulationConfig, CollisionEffectType, CollisionColorMode } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';

interface ImpactFXSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const ImpactFXSection: React.FC<ImpactFXSectionProps> = ({ config, setConfig }) => {
  const effects: CollisionEffectType[] = [
    'RIPPLE', 'GRAVITY_PARTICLES', 'ORBITAL_SPIN', 'ELASTIC_RING', 'LIQUID_SPLASH', 
    'SPARKS', 'SHATTER_GLASS', 'PIXEL_CRUMBLE', 'NEON_PULSE', 'VORTEX_SUCK', 
    'FIREWORKS', 'SMOKE_PUFF', 'GLITCH_FRAGMENTS', 'LIGHTNING_BOLT', 'BUBBLE_POP', 
    'SPIRAL_GALAXY', 'SONIC_WAVE', 'QUANTUM_TUNNEL', 'DNA_HELIX', 'MATRIX_RAIN', 
    'PLASMA_BLOB', 'CRYSTAL_SHARDS', 'GHOST_SOULS', 'CYBER_HEX', 'INK_SPLAT', 
    'SUPERNOVA', 'CONFETTI_CANNON'
  ];

  const colorModes: CollisionColorMode[] = ['BALL_COLOR', 'RAINBOW', 'CUSTOM_SOLID', 'CUSTOM_GRADIENT'];

  const ColorInput = ({ label, value, onChange }: { label: string, value: string, onChange: (v: string) => void }) => (
    <ControlGroup label={label}>
        <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: value }} />
            <input type="color" value={value} onChange={e => onChange(e.target.value)} className="flex-1 h-8 opacity-0 absolute cursor-pointer" />
            <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`flex-1 px-2 py-1 text-[9px] font-mono border rounded-lg ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`} />
        </div>
    </ControlGroup>
  );

  return (
    <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border-2 bg-pink-500/10 border-pink-500/30 mb-4 shadow-lg shadow-pink-500/5">
      <div className="flex items-center justify-between">
          <SectionLabel icon="fa-burst" darkMode={config.darkMode}>Collision Impact FX</SectionLabel>
          <span className="bg-pink-600 text-white text-[8px] font-black uppercase px-2 py-0.5 rounded-full animate-pulse">New!</span>
      </div>
      
      <ControlGroup label="Select Effect Style">
        <select 
            value={config.collisionEffect || 'RIPPLE'} 
            onChange={e => setConfig(p => ({...p, collisionEffect: e.target.value as CollisionEffectType}))}
            className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
        >
            {effects.map(e => <option key={e} value={e}>{e.replace('_', ' ')}</option>)}
        </select>
      </ControlGroup>

      <div className="grid grid-cols-2 gap-3">
          <SmartInput label="Duration (ms)" min={100} max={2000} value={config.collisionEffectDuration || 800} onChange={v => setConfig(p => ({...p, collisionEffectDuration: v}))} darkMode={config.darkMode} />
          <SmartInput label="Particles" min={1} max={100} value={config.collisionEffectParticleCount || 20} onChange={v => setConfig(p => ({...p, collisionEffectParticleCount: v}))} darkMode={config.darkMode} />
      </div>
      <SmartInput label="Gravity" min={0} max={2} step={0.1} value={config.collisionEffectGravity ?? 0.5} onChange={v => setConfig(p => ({...p, collisionEffectGravity: v}))} darkMode={config.darkMode} />

      <div className="h-px bg-white/10 my-1" />
      <span className="text-[9px] font-black uppercase opacity-60">Effect Coloring</span>
      
      <ControlGroup label="Color Mode">
        <select 
            value={config.collisionEffectColorMode || 'BALL_COLOR'} 
            onChange={e => setConfig(p => ({...p, collisionEffectColorMode: e.target.value as CollisionColorMode}))}
            className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
        >
            {colorModes.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
        </select>
      </ControlGroup>

      {(config.collisionEffectColorMode === 'CUSTOM_SOLID' || config.collisionEffectColorMode === 'CUSTOM_GRADIENT') && (
          <ColorInput label="Primary Color" value={config.collisionEffectColor1 || '#ff0000'} onChange={v => setConfig(p => ({...p, collisionEffectColor1: v}))} />
      )}
      
      {config.collisionEffectColorMode === 'CUSTOM_GRADIENT' && (
          <ColorInput label="Secondary Color" value={config.collisionEffectColor2 || '#ffff00'} onChange={v => setConfig(p => ({...p, collisionEffectColor2: v}))} />
      )}
    </div>
  );
};
